import { useCallback } from 'react'
import { SingleValue } from 'react-select'
import { selectF, voidF, inputFocusF, voidBooleanF, voidNumberF } from 'types/FunctionTypes'
import { getNumberFromString, getTimestampFromNumber } from 'utils/dateUtil'
import { convertNumber } from 'utils/numberUtil'
import { useDispatch, useSelector } from 'store'
import * as PageAction from 'slicers/PageSlice'
import * as SearchJuryoAction from 'slicers/SearchSlicers/SearchJuryoSlice'
import * as TableAction from 'slicers/TableSlicers/TableSlice'
import * as TableJuryoAction from 'slicers/TableSlicers/TableJuryoSlice'
import * as ModalFileAction from 'slicers/ModalFileSlice'
import { getRequestJoken, responseJuryo } from 'utils/requestUtil'
import { F_NUM } from 'assets/FormatNumbers'
import { useHistory, useLocation } from 'react-router-dom'
import { baseInfo, csvInfo, searchJoken, select } from 'types/SearchTypes'
import { postFetcher, response } from 'utils/apiUtil'

// 年月日 //================================//
export const useDate = (): [string, string, inputFocusF, inputFocusF] => {
  const dispatch = useDispatch()
  const NOHIN_YMD_FROM = useSelector((state) => state.SearchJuryoReducer.searchJoken.NOHIN_YMD_FROM)
  const NOHIN_YMD_TO = useSelector((state) => state.SearchJuryoReducer.searchJoken.NOHIN_YMD_TO)
  const changeFromDate = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.preventDefault()
      const max = 99991231
      const min = 10000101
      let value = getNumberFromString(e.target.value)
      if (max < value) value = max
      if (min > value) value = F_NUM
      dispatch(SearchJuryoAction.changeNohinYmdFrom(value === F_NUM ? '' : getTimestampFromNumber(convertNumber(value, 8))))

      // FROMがTOより遅くなる場合、TOをFROMに合わせる
      if (NOHIN_YMD_TO !== F_NUM && value > NOHIN_YMD_TO) dispatch(SearchJuryoAction.changeNohinYmdTo(value === F_NUM ? '' : getTimestampFromNumber(convertNumber(value, 8))))
    },
    [NOHIN_YMD_FROM, NOHIN_YMD_TO],
  )
  const changeToDate = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.preventDefault()
      const max = 99991231
      const min = 10000101
      let value = getNumberFromString(e.target.value)
      if (max < value) value = max
      if (min > value) value = F_NUM
      dispatch(SearchJuryoAction.changeNohinYmdTo(value === F_NUM ? '' : getTimestampFromNumber(convertNumber(value, 8))))

      // TOがFROMより早くなる場合、FROMをTOに合わせる
      if (value !== F_NUM && NOHIN_YMD_FROM > value) dispatch(SearchJuryoAction.changeNohinYmdFrom(getTimestampFromNumber(convertNumber(value, 8))))
    },
    [NOHIN_YMD_FROM, NOHIN_YMD_TO],
  )
  return [getTimestampFromNumber(convertNumber(NOHIN_YMD_FROM, 8)), getTimestampFromNumber(convertNumber(NOHIN_YMD_TO, 8)), changeFromDate, changeToDate]
}

// 年月日明細 //================================//
export const useDateDetail = (): [string, string, voidNumberF, voidNumberF] => {
  const dispatch = useDispatch()
  const NOHIN_YMD_FROM = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.NOHIN_YMD_FROM)
  const NOHIN_YMD_TO = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.NOHIN_YMD_TO)
  const changeFromDate = useCallback(
    (value: number) => {
      dispatch(SearchJuryoAction.changeNohinYmdFromDetail(getTimestampFromNumber(convertNumber(value, 8))))
    },
    [NOHIN_YMD_FROM],
  )
  const changeToDate = useCallback(
    (value: number) => {
      dispatch(SearchJuryoAction.changeNohinYmdToDetail(getTimestampFromNumber(convertNumber(value, 8))))
    },
    [NOHIN_YMD_TO],
  )
  return [getTimestampFromNumber(convertNumber(NOHIN_YMD_FROM, 8)), getTimestampFromNumber(convertNumber(NOHIN_YMD_TO, 8)), changeFromDate, changeToDate]
}

// 得意先 //================================//
export const useTokuisaki = (): [string, selectF] => {
  const dispatch = useDispatch()
  const TOKUI_CD = useSelector((state) => state.SearchJuryoReducer.searchJoken.TOKUI_CD)
  const changeTokui = useCallback(
    (targetValue: SingleValue<any>) => {
      dispatch(SearchJuryoAction.changeTokuisakiCode(targetValue!.value))
      dispatch(SearchJuryoAction.changeTenpoCode(F_NUM))
    },
    [TOKUI_CD],
  )
  return [String(TOKUI_CD), changeTokui]
}

// 得意先明細 //================================//
export const useTokuisakiDetail = (): [string, selectF] => {
  const dispatch = useDispatch()
  const TOKUI_CD = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.TOKUI_CD)
  const changeTokui = useCallback(
    (r: select) => {
      dispatch(SearchJuryoAction.changeTokuisakiCodeDetail(r))
    },
    [TOKUI_CD],
  )
  return [String(TOKUI_CD), changeTokui]
}

// 店舗 //================================//
export const useTenpo = (): [string, selectF] => {
  const dispatch = useDispatch()
  const TENPO_CD = useSelector((state) => state.SearchJuryoReducer.searchJoken.TEN_CD)
  const changeTenpoCode = useCallback(
    (targetValue: SingleValue<any>) => {
      dispatch(SearchJuryoAction.changeTenpoCode(targetValue!.value))
    },
    [TENPO_CD],
  )
  return [String(TENPO_CD), changeTenpoCode]
}

// 店舗明細 //================================//
export const useTenpoDetail = (): [string, selectF] => {
  const dispatch = useDispatch()
  const TENPO_CD = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.TEN_CD)
  const changeTenpoCode = useCallback(
    (r: select) => {
      dispatch(SearchJuryoAction.changeTenpoCodeDetail(r))
    },
    [TENPO_CD],
  )
  return [String(TENPO_CD), changeTenpoCode]
}

// 事業所 //================================//
export const useJigyosho = (): [string, selectF] => {
  const dispatch = useDispatch()
  const JIGYO_CD = useSelector((state) => state.SearchJuryoReducer.searchJoken.JIGYO_CD)
  const changeJigyo = useCallback(
    (targetValue: SingleValue<any>) => {
      dispatch(SearchJuryoAction.changeJigyoshoCode(targetValue!.value))
    },
    [JIGYO_CD],
  )
  return [String(JIGYO_CD), changeJigyo]
}

// JPコード //================================//
export const useShohin = (): [string, selectF] => {
  const dispatch = useDispatch()
  const SHOHIN_CD = useSelector((state) => state.SearchJuryoReducer.searchJoken.SHOHIN_CD)
  const changeShohinCode = useCallback(
    (targetValue: SingleValue<any>) => {
      dispatch(SearchJuryoAction.changeShohinCode(targetValue!.value))
    },
    [SHOHIN_CD],
  )
  return [String(SHOHIN_CD), changeShohinCode]
}

// JPコード明細 //================================//
export const useShohinDetail = (): [string, selectF] => {
  const dispatch = useDispatch()
  const SHOHIN_CD = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.SHOHIN_CD)
  const changeShohinCode = useCallback(
    (targetValue: SingleValue<any>) => {
      dispatch(SearchJuryoAction.changeShohinCodeDetail(targetValue!.value))
    },
    [SHOHIN_CD],
  )
  return [String(SHOHIN_CD), changeShohinCode]
}

// 伝票番号 //================================//
export const useDenpyoNo = (): [string, selectF] => {
  const dispatch = useDispatch()
  const DENPYO_NO = useSelector((state) => state.SearchJuryoReducer.searchJoken.DENPYO_NO)
  const changeDenpyoNo = useCallback(
    (targetValue: SingleValue<any>) => {
      dispatch(SearchJuryoAction.changeDenpyoNo(targetValue!.value))
    },
    [DENPYO_NO],
  )
  return [DENPYO_NO, changeDenpyoNo]
}

// 状態 //================================//
export const useJuryo = (): [boolean, boolean, boolean, boolean, voidF, voidF, voidF, voidF] => {
  const dispatch = useDispatch()
  const mijuryoCheck = useSelector((state) => state.SearchJuryoReducer.searchJoken.JURYO_KBN.mijuryoCheck)
  const juryoCheck = useSelector((state) => state.SearchJuryoReducer.searchJoken.JURYO_KBN.juryoCheck)
  const naiyoCheck = useSelector((state) => state.SearchJuryoReducer.searchJoken.JURYO_KBN.naiyoCheck)
  const keppinCheck = useSelector((state) => state.SearchJuryoReducer.searchJoken.keppinCheck)

  const changeOtherCheck = (clickCheckName: string, clickCheck: boolean, otherCheck: boolean[]) => {
    // clickCheck、otherCheckがすべてfalseの場合、clickCheckName以外をtrueにする
    if (!clickCheck) return
    if (
      otherCheck.some((check) => {
        return check
      })
    )
      return
    if (clickCheckName !== 'mijuryoCheck') dispatch(SearchJuryoAction.changeMijuryoCheck())
    if (clickCheckName !== 'juryoCheck') dispatch(SearchJuryoAction.changeJuryoCheck())
    if (clickCheckName !== 'naiyoCheck') dispatch(SearchJuryoAction.changeNaiyoCheck())
  }
  const changeMijuryoCheck = useCallback(() => {
    changeOtherCheck('mijuryoCheck', mijuryoCheck, [juryoCheck, naiyoCheck])
    dispatch(SearchJuryoAction.changeMijuryoCheck())
  }, [mijuryoCheck, juryoCheck, naiyoCheck])

  const changeJuryoCheck = useCallback(() => {
    changeOtherCheck('juryoCheck', juryoCheck, [mijuryoCheck, naiyoCheck])
    dispatch(SearchJuryoAction.changeJuryoCheck())
  }, [mijuryoCheck, juryoCheck, naiyoCheck])

  const changeNaiyoCheck = useCallback(() => {
    changeOtherCheck('naiyoCheck', naiyoCheck, [mijuryoCheck, juryoCheck])
    dispatch(SearchJuryoAction.changeNaiyoCheck())
  }, [mijuryoCheck, juryoCheck, naiyoCheck])

  const changeKeppinCheck = useCallback(() => {
    dispatch(SearchJuryoAction.changeKeppinCheck())
  }, [keppinCheck])

  return [
    mijuryoCheck, //
    juryoCheck,
    naiyoCheck,
    keppinCheck,
    changeMijuryoCheck,
    changeJuryoCheck,
    changeNaiyoCheck,
    changeKeppinCheck,
  ]
}

// 状態明細 //================================//
export const useJuryoDetail = (): [boolean, boolean, boolean, boolean, voidBooleanF, voidBooleanF, voidBooleanF, voidBooleanF] => {
  const dispatch = useDispatch()
  const mijuryoCheck = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.JURYO_KBN.mijuryoCheck)
  const juryoCheck = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.JURYO_KBN.juryoCheck)
  const naiyoCheck = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.JURYO_KBN.naiyoCheck)
  const keppinCheck = useSelector((state) => state.SearchJuryoReducer.searchJokenDetail.keppinCheck)

  const changeMijuryoCheck = useCallback((bool: boolean) => {
    dispatch(SearchJuryoAction.changeMijuryoCheckDetail(bool))
  }, [])

  const changeJuryoCheck = useCallback((bool: boolean) => {
    dispatch(SearchJuryoAction.changeJuryoCheckDetail(bool))
  }, [])

  const changeNaiyoCheck = useCallback((bool: boolean) => {
    dispatch(SearchJuryoAction.changeNaiyoCheckDetail(bool))
  }, [])

  const changeKeppinCheck = useCallback((bool: boolean) => {
    dispatch(SearchJuryoAction.changeKeppinCheckDetail(bool))
  }, [])

  return [
    mijuryoCheck, //
    juryoCheck,
    naiyoCheck,
    keppinCheck,
    changeMijuryoCheck,
    changeJuryoCheck,
    changeNaiyoCheck,
    changeKeppinCheck,
  ]
}

// ボタン項目押下処理 //================================//
export const useSearch = (): [voidF, voidF, voidF] => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.PageReducer.loading)
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const isSuper = useSelector((state) => state.LoginReducer.isSuper)

  // CSV登録ボタン押下処理 //================================//
  const clickInputCSV = useCallback(() => {
    if (!loading) dispatch(ModalFileAction.open())
  }, [loading])

  // 検索条件リセットボタン押下処理 //================================//
  const clickReset = useCallback(() => {
    dispatch(PageAction.format())
    dispatch(SearchJuryoAction.initialize({ isJapell: isJapell, isSuper: isSuper, JIGYO_CD: keyInfo[0].JIGYO_CD })) // 初期状態に設定
    dispatch(TableJuryoAction.format())
    dispatch(TableAction.format())
  }, [isJapell])

  const history = useHistory()
  // 戻るボタン押下処理 //================================//
  const clickBack = useCallback(() => {
    dispatch(PageAction.format())
    dispatch(TableJuryoAction.format())
    dispatch(SearchJuryoAction.closeDetail())
    history.push('/juryohyo')
  }, [])

  return [clickReset, clickInputCSV, clickBack]
}

// 明細表示判定 //================================//
export const useDetailView = (): [boolean, voidBooleanF] => {
  const dispatch = useDispatch()
  const detailView = useSelector((state) => state.SearchJuryoReducer.detailView)
  const changeDetailView = useCallback(
    (detailView: boolean) => {
      dispatch(SearchJuryoAction.changeDetailView(detailView))
    },
    [detailView],
  )
  return [detailView, changeDetailView]
}

// 押下した納品日 //================================//
export const useClickNohinYmd = (): [number, voidNumberF] => {
  const dispatch = useDispatch()
  const clickNohinYmd = useSelector((state) => state.SearchJuryoReducer.clickNohinYmd)
  const changeClickNohinYmd = useCallback(
    (clickNohinYmd: number) => {
      dispatch(SearchJuryoAction.changeClickNohinYmd(clickNohinYmd))
    },
    [clickNohinYmd],
  )
  return [clickNohinYmd, changeClickNohinYmd]
}

// 押下した得意先コード //================================//
export const useClickTokuisaki = (): [number, voidNumberF] => {
  const dispatch = useDispatch()
  const clickTokuiCd = useSelector((state) => state.SearchJuryoReducer.clickTokuiCd)
  const changeClickTokuisakiCode = useCallback(
    (clickTokuiCd: number) => {
      dispatch(SearchJuryoAction.changeClickTokuisakiCode(clickTokuiCd))
    },
    [clickTokuiCd],
  )
  return [clickTokuiCd, changeClickTokuisakiCode]
}

// 押下した店舗コード //================================//
export const useClickTenpo = (): [number, voidNumberF] => {
  const dispatch = useDispatch()
  const clickTenCd = useSelector((state) => state.SearchJuryoReducer.clickTenCd)
  const changeClickTenpoCode = useCallback(
    (clickTenCd: number) => {
      dispatch(SearchJuryoAction.changeClickTenpoCode(clickTenCd))
    },
    [clickTenCd],
  )
  return [clickTenCd, changeClickTenpoCode]
}

// 検索部分開閉 //================================//
export const useExpand = (): [boolean, voidBooleanF] => {
  const dispatch = useDispatch()
  const expand = useSelector((state) => state.SearchJuryoReducer.expand)
  const detailView = useSelector((state) => state.SearchJuryoReducer.detailView)
  const changeExpand = useCallback(
    (bool: boolean) => {
      dispatch(SearchJuryoAction.changeExpand(bool))
    },
    [expand, detailView],
  )
  return [expand, changeExpand]
}

// 明細画面の検索部分開閉 //================================//
export const useExpandDetail = (): [boolean, voidBooleanF] => {
  const dispatch = useDispatch()
  const expandDetail = useSelector((state) => state.SearchJuryoReducer.expandDetail)
  const detailView = useSelector((state) => state.SearchJuryoReducer.detailView)
  const changeExpandDetail = useCallback(
    (bool: boolean) => {
      dispatch(SearchJuryoAction.changeExpandDetail(bool))
    },
    [expandDetail, detailView],
  )
  return [expandDetail, changeExpandDetail]
}

// サマリフェッチャー //================================//
type fetchResp = { pullDown: baseInfo; table: responseJuryo[]; allCount: number; csvInfo: csvInfo }
export const useFetcherJuryo = (): [searchJoken, () => Promise<response<fetchResp>>] => {
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)
  const searchJoken = useSelector((state) => state.SearchJuryoReducer.searchJoken)
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const page = useSelector((state) => state.TableReducer.page)
  const perPage = useSelector((state) => state.TableReducer.perPage)
  return [searchJoken, postFetcher<fetchResp>('postJuryoHyoInfo', getRequestJoken(isJapell, false, keyInfo, searchJoken, page, perPage))]
}

// サマリ初期描画調整 //================================//
export const useInitProcessSummary = () => {
  const dispatch = useDispatch()
  const detailView = useSelector((state) => state.SearchJuryoReducer.detailView)
  const initProcess = useCallback(() => {
    if (detailView) {
      dispatch(SearchJuryoAction.closeDetail())
      dispatch(TableJuryoAction.format())
    }
  }, [detailView])
  return initProcess
}

// 明細初期描画調整 //================================//
export const useInitProcessDetail = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const pathname = location.pathname
  const pathArray = pathname.split('/')
  const nohinYmdTokuiCdTenCd = pathArray[pathArray.length - 1]
  const clickNohinYmd = useSelector((state) => state.SearchJuryoReducer.clickNohinYmd)
  const clickTokuiCd = useSelector((state) => state.SearchJuryoReducer.clickTokuiCd)
  const clickTenCd = useSelector((state) => state.SearchJuryoReducer.clickTenCd)
  const [NOHIN_YMD, TOKUI_CD, TEN_CD] = nohinYmdTokuiCdTenCd.split('-')
  const initProcess = useCallback(() => {
    if (Number(NOHIN_YMD) !== clickNohinYmd || Number(TOKUI_CD) !== clickTokuiCd || Number(TEN_CD) !== clickTenCd) {
      // URLとクリックしたデータが一致しないのでサマリに戻す
      dispatch(SearchJuryoAction.closeDetail())
      dispatch(TableJuryoAction.format())
      history.push('/juryohyo')
    }
  }, [pathname, clickNohinYmd, clickTokuiCd, clickTenCd])
  return initProcess
}
